import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Layout, Typography, Button, Card, Row, Col, Space, Avatar, message, Badge, Tooltip, Spin, Divider, Dropdown, Menu } from 'antd';
import { PhoneOutlined, UserOutlined, LogoutOutlined, BellOutlined, MailOutlined, HomeOutlined, SafetyOutlined, ClockCircleOutlined, MenuOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

// Navigation items configuration
const NAVIGATION_ITEMS = [
  { key: 'profile', label: 'Profile', path: '/profile', icon: <UserOutlined /> },
  { key: 'services', label: 'Services & Resources', path: 'https://app.nhguide.org/ServiceProviders', icon: <HomeOutlined /> },
  { key: 'privacy', label: 'Privacy & Data', path: '/DataPrivacy', icon: <SafetyOutlined /> },
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleOnHover = {
  whileHover: { scale: 1.02, y: -2 },
  whileTap: { scale: 0.98 }
};

const mobileMenuAnimation = {
  initial: { opacity: 0, scaleY: 0, transformOrigin: 'top' },
  animate: { opacity: 1, scaleY: 1 },
  exit: { opacity: 0, scaleY: 0 },
  transition: { duration: 0.2, ease: 'easeInOut' }
};

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(135deg, #f3f7fe 0%, #e0e7ff 100%);
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(96, 165, 250, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(147, 197, 253, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const StyledHeader = styled(Header)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 0 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #e6efff;
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 80px;

  .header-brand {
    display: flex;
    align-items: center;
    gap: 20px;

    .ant-avatar {
      background-color: #60a5fa;
      border: 3px solid #ffffff;
      box-shadow: 
        0 4px 15px rgba(96, 165, 250, 0.3),
        0 0 0 1px rgba(96, 165, 250, 0.2);
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.05);
        box-shadow: 
          0 6px 20px rgba(96, 165, 250, 0.4),
          0 0 0 2px rgba(96, 165, 250, 0.3);
      }
    }

    .header-title {
      margin: 0;
      color: #1e40af;
      font-weight: 800;
      font-size: 28px;
      letter-spacing: -0.5px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .header-nav {
    display: flex;
    align-items: center;
    gap: 32px;

    .header-nav-link {
      color: #1e293b;
      font-weight: 600;
      padding: 12px 20px;
      text-decoration: none;
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      font-size: 15px;
      background: transparent;
      border: 2px solid transparent;

      &:hover {
        color: #3b82f6;
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.2);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
      }

      &::after {
        content: '';
        position: absolute;
        bottom: 8px;
        left: 50%;
        width: 0;
        height: 2px;
        background: linear-gradient(90deg, #3b82f6, #60a5fa);
        border-radius: 1px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        transform: translateX(-50%);
      }

      &:hover::after {
        width: 60%;
      }
    }

    .logout-btn {
      color: #dc2626;
      font-weight: 600;
      padding: 12px 20px;
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border: 2px solid transparent;

      &:hover {
        color: #ffffff;
        background: #dc2626;
        border-color: #dc2626;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
      }
    }
  }

  .mobile-menu-button {
    display: none;
    padding: 8px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid #e6efff;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    height: 64px;
    
    .header-brand {
      gap: 10px;
      
      .header-title {
        font-size: 20px;
      }
      
      .ant-avatar {
        width: 36px;
        height: 36px;
      }
    }
    
    .header-nav {
      display: none;
    }
    
    .mobile-menu-button {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const HeroSection = styled(motion.div)`
  background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 50%, #1e40af 100%);
  border-radius: 24px;
  padding: 60px 40px;
  color: #ffffff;
  box-shadow: 
    0 20px 40px rgba(30, 64, 175, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  margin: 0 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.05) 50%, transparent 70%);
    pointer-events: none;
  }

  .hero-banner {
    width: 100%;
    position: relative;
    z-index: 2;
  }

  .hero-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    flex-wrap: wrap;
  }

  .hero-assistant {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    min-width: 180px;
    padding: 30px 20px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    .ant-avatar {
      background-color: #ffffff;
      border: 4px solid rgba(255, 255, 255, 0.4);
      box-shadow: 
        0 8px 25px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(255, 255, 255, 0.2);
    }
  }

  .hero-center {
    flex: 1;
    text-align: center;
    max-width: 600px;
    padding: 0 20px;
  }

  .hero-title {
    color: #ffffff;
    margin: 0 0 20px 0;
    font-weight: 800;
    line-height: 1.2;
    font-size: clamp(28px, 4vw, 48px);
    text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    letter-spacing: -1px;
  }

  .hero-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 18px;
    display: block;
    margin-bottom: 32px;
    font-weight: 500;
    line-height: 1.6;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .hero-text {
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .hero-subtext {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    font-weight: 500;
  }

  .hero-support-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.4);
    color: #ffffff;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.6);
      color: #ffffff;
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    }
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
    margin: 0 8px;
    border-radius: 16px;
    
    .hero-content {
      flex-direction: column;
      text-align: center;
      gap: 20px;
    }
    
    .hero-assistant {
      min-width: auto;
      width: 100%;
      max-width: 240px;
      padding: 20px 16px;
      
      .ant-avatar {
        width: 60px;
        height: 60px;
      }
    }
    
    .hero-title {
      font-size: clamp(22px, 6vw, 28px);
      margin-bottom: 16px;
    }
    
    .hero-subtitle {
      font-size: 14px;
      margin-bottom: 24px;
      max-width: 100%;
    }
    
    .hero-text {
      font-size: 14px;
    }
    
    .hero-subtext {
      font-size: 12px;
    }
    
    .hero-support-btn {
      padding: 10px 16px;
      font-size: 12px;
      height: 44px;
    }
  }
`;

const UserInfoSection = styled(motion.div)`
  background: transparent;
  padding: 32px 0;
  margin: 32px 0 0;
  position: relative;

  .hero-user-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    flex-wrap: wrap;
    max-width: 800px;
    margin: 0 auto;
    padding: 24px 32px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.3);

    .user-profile {
      display: flex;
      align-items: center;
      gap: 20px;
      flex: 1;

      .ant-avatar {
        background-color: #60a5fa;
        border: 3px solid rgba(255, 255, 255, 0.8);
        box-shadow: 
          0 6px 20px rgba(96, 165, 250, 0.3),
          0 0 0 1px rgba(255, 255, 255, 0.5);
        flex-shrink: 0;
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.05);
          box-shadow: 
            0 8px 25px rgba(96, 165, 250, 0.4),
            0 0 0 2px rgba(96, 165, 250, 0.3);
        }
      }

      .user-details {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .user-name {
          color: #1e293b;
          font-size: 20px;
          font-weight: 800;
          margin: 0;
          background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .user-info-item {
          color: #64748b;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 8px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 8px;
          border: 1px solid rgba(100, 116, 139, 0.2);

          .anticon {
            color: #3b82f6;
            font-size: 14px;
          }
        }
      }
    }

    .user-actions {
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: flex-end;

      .status-badge {
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
        padding: 6px 12px;
        border-radius: 16px;
        font-size: 12px;
        font-weight: 700;
        border: 1px solid rgba(16, 185, 129, 0.3);
        display: flex;
        align-items: center;
        gap: 6px;

        &::before {
          content: '';
          width: 6px;
          height: 6px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      }

      .contact-support-btn {
        background: #3b82f6;
        border-color: #3b82f6;
        color: #ffffff;
        padding: 8px 16px;
        font-weight: 600;
        font-size: 14px;
        border-radius: 12px;
        box-shadow: 0 4px 15px rgba(59, 130, 246, 0.25);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          background: #2563eb;
          border-color: #2563eb;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
          color: #ffffff;
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 16px 0;
    margin: 16px 0 0;
    
    .hero-user-info {
      flex-direction: column;
      text-align: center;
      gap: 16px;
      margin: 0 8px;
      padding: 16px;
      border-radius: 12px;
      
      .user-profile {
        flex-direction: column;
        text-align: center;
        
        .ant-avatar {
          width: 56px;
          height: 56px;
        }
        
        .user-details {
          gap: 6px;
          
          .user-name {
            font-size: 16px;
          }
          
          .user-info-item {
            font-size: 12px;
            padding: 4px 6px;
            justify-content: center;
            
            .anticon {
              font-size: 12px;
            }
          }
        }
      }
      
      .user-actions {
        align-items: center;
        flex-direction: row;
        justify-content: center;
        flex-wrap: wrap;
        gap: 8px;
        
        .status-badge {
          font-size: 10px;
          padding: 4px 8px;
        }
        
        .contact-support-btn {
          font-size: 12px;
          padding: 6px 12px;
          height: 44px;
        }
      }
    }
  }
`;

const SupportCasesSection = styled(motion.div)`
  background: transparent;
  padding: 24px 0;
  margin: 24px 0;
  position: relative;
  text-align: center;

  .support-cases-container {
    max-width: 450px;
    margin: 0 auto;
    padding: 20px 28px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    border-radius: 18px;
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.4);

    .support-title {
      color: #1e293b;
      font-size: 22px;
      font-weight: 800;
      margin-bottom: 12px;
      background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.3px;
    }

    .support-description {
      color: #64748b;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 20px;
      line-height: 1.5;
    }

    .support-cta-btn {
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
      border: none;
      color: #ffffff;
      font-weight: 700;
      padding: 12px 24px;
      height: auto;
      font-size: 14px;
      border-radius: 14px;
      box-shadow: 
        0 6px 18px rgba(59, 130, 246, 0.25),
        0 0 0 1px rgba(255, 255, 255, 0.2);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
        color: #ffffff;
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(59, 130, 246, 0.35);
      }

      .anticon {
        margin-right: 6px;
        font-size: 16px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 16px 0;
    margin: 16px 0;
    
    .support-cases-container {
      margin: 0 8px;
      padding: 16px;
      border-radius: 12px;
      
      .support-title {
        font-size: 18px;
        margin-bottom: 8px;
      }
      
      .support-description {
        font-size: 12px;
        margin-bottom: 16px;
      }
      
      .support-cta-btn {
        font-size: 12px;
        padding: 10px 16px;
        height: 44px;
        
        .anticon {
          font-size: 14px;
        }
      }
    }
  }
`;

const StyledContent = styled(Content)`
  padding: 0 60px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    margin-top: 24px;
  }
`;

const CardsSection = styled.div`
  margin-top: 60px;
  
  .section-title {
    text-align: center;
    margin-bottom: 50px;
    
    h2 {
      color: #1e293b;
      font-weight: 900;
      font-size: 32px;
      margin-bottom: 14px;
      background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      letter-spacing: -0.5px;
    }
    
    p {
      color: #64748b;
      font-size: 16px;
      font-weight: 500;
      max-width: 550px;
      margin: 0 auto;
      line-height: 1.6;
    }
  }

  @media (max-width: 768px) {
    margin-top: 40px;
    
    .section-title {
      margin-bottom: 30px;
      
      h2 {
        font-size: 22px;
      }
      
      p {
        font-size: 14px;
        max-width: 100%;
        padding: 0 8px;
      }
    }
  }
`;

const StyledFooter = styled(Footer)`
  text-align: center;
  padding: 40px 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  border-top: 1px solid #e6efff;
  margin-top: 60px;

  .ant-btn-link {
    color: #64748b;
    font-weight: 600;
    padding: 10px 18px;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 14px;

    &:hover {
      color: #3b82f6;
      background: rgba(59, 130, 246, 0.1);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
    }
  }

  @media (max-width: 768px) {
    padding: 24px 0;
    margin-top: 40px;
    
    .ant-btn-link {
      font-size: 12px;
      padding: 8px 12px;
    }
  }
`;

// Custom hook for user data management
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('Fetching user data from /backend/get_profile.php');
        
        const response = await fetch('/backend/get_profile.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers));

        if (!response.ok) {
          if (response.status === 401) {
            console.log('User not authenticated, redirecting to login');
            navigate('/login');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('API Response:', JSON.stringify(data, null, 2));
        
        if (data.userName || data.nhGuideNumber) {
          const location = [data.street, data.city].filter(Boolean).join(', ') || 'Location not set';
          
          const mappedUserData = {
            name: data.userName || 'User',
            location: location,
            nhNumber: data.nh_guide_number || data.nhGuideNumber || 'Not assigned',
            avatar: data.profile_picture || data.profilePicture || null,
            notifications: data.notifications || 0,
            lastLogin: new Date().toLocaleDateString(),
          };

          console.log('Mapped user data:', mappedUserData);
          setUserData(mappedUserData);
        } else {
          console.error('API returned no valid user data or success: false', data);
          
          if (data.message && (data.message.toLowerCase().includes('not logged in') || data.message.toLowerCase().includes('authentication'))) {
            console.log('Authentication issue, redirecting to login');
            navigate('/login');
            return;
          }
          
          console.log('Using fallback user data due to API error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
          });
          
          message.warning({
            content: data.message || 'Some user information could not be loaded',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network error - please check your connection');
          message.error({
            content: 'Network error - please check your connection',
            duration: 3,
          });
        } else if (err.message.includes('401')) {
          console.log('Authentication error, redirecting to login');
          navigate('/login');
          return;
        } else {
          console.log('Using fallback user data due to error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
          });
          
          message.warning({
            content: 'Some user information could not be loaded',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  return { userData, loading, error };
};

// Navigation Header Component
const NavigationHeader = memo(({ onLogout, userData, loading }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const menuItems = [
    ...NAVIGATION_ITEMS.map(item => ({
      key: item.key,
      label: item.label,
      icon: item.icon,
      onClick: () => {
        navigate(item.path);
        setMenuOpen(false);
      },
      style: {
        fontSize: '14px',
        fontWeight: 600,
        padding: '10px 16px',
        borderRadius: '8px',
        margin: '4px 8px',
        color: '#1e293b',
      }
    })),
    {
      key: 'notifications',
      label: (
        <Badge count={userData?.notifications || 0} size="small">
          Notifications
        </Badge>
      ),
      icon: <BellOutlined />,
      onClick: () => {
        navigate('/notifications');
        setMenuOpen(false);
      },
      style: {
        fontSize: '14px',
        fontWeight: 600,
        padding: '10px 16px',
        borderRadius: '8px',
        margin: '4px 8px',
        color: '#1e293b',
      }
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        onLogout();
        setMenuOpen(false);
      },
      style: {
        fontSize: '14px',
        fontWeight: 600,
        padding: '10px 16px',
        borderRadius: '8px',
        margin: '4px 8px',
        color: '#dc2626',
      }
    }
  ];

  return (
    <StyledHeader>
      <motion.div 
        className="header-brand"
        {...scaleOnHover}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <Avatar 
          icon={<UserOutlined />} 
          className="ant-avatar" 
          size="large"
          src={userData?.avatar}
        />
        <Title level={4} className="header-title">
          NH Guide
        </Title>
      </motion.div>
      <nav className="header-nav">
        {NAVIGATION_ITEMS.map(item => (
          <motion.div
            key={item.key}
            {...scaleOnHover}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <Link
              to={item.path}
              className="header-nav-link"
            >
              {item.label}
            </Link>
          </motion.div>
        ))}
        <Tooltip title="Notifications" placement="bottom">
          <motion.div {...scaleOnHover}>
            <Badge count={userData?.notifications || 0} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                className="header-nav-link"
                style={{ padding: '12px' }}
              />
            </Badge>
          </motion.div>
        </Tooltip>
        <motion.div {...scaleOnHover}>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            className="logout-btn"
            loading={loading}
          >
            Logout
          </Button>
        </motion.div>
      </nav>
      <motion.div
        className="mobile-menu-button"
        {...scaleOnHover}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          overlayStyle={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            borderRadius: '12px', 
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)', 
            minWidth: '200px',
            maxWidth: '90vw',
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            size="large"
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      </motion.div>
    </StyledHeader>
  );
});

// Hero Banner Component
const HeroBanner = memo(({ loading }) => {
  if (loading) {
    return (
      <HeroSection
        {...fadeInUp}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="hero-banner">
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <Spin size="large" />
            <Text style={{ color: '#ffffff', display: 'block', marginTop: 24, fontSize: 18 }}>
              Loading your dashboard...
            </Text>
          </div>
        </div>
      </HeroSection>
    );
  }

  return (
    <HeroSection
      {...fadeInUp}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="hero-banner">
        <motion.div 
          className="hero-content"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <motion.div 
            className="hero-center"
            variants={fadeInUp}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <Title level={1} className="hero-title">
              Navigating Homelessness Guide
            </Title>
            <Text className="hero-subtitle">
              Comprehensive resources and support to help you regain stable housing and rebuild your life with dignity
            </Text>
            <Space size={20} style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 5 }} 
                whileTap={{ scale: 0.9 }}
                variants={fadeInUp}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <Tooltip title="Emergency Support" placement="bottom">
                  <Button
                    type="default"
                    shape="circle"
                    href="/support"
                    icon={<PhoneOutlined style={{ color: '#ffffff', fontSize: 20 }} />}
                    className="hero-support-btn"
                    size="large"
                    style={{ width: 60, height: 60, padding: 0 }}
                  />
                </Tooltip>
              </motion.div>
            </Space>
          </motion.div>
          
          <motion.div 
            className="hero-assistant"
            variants={fadeInUp}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <Avatar
              icon={<UserOutlined />}
              size={80}
              className="ant-avatar"
              src="components/nhguidelogo.jpg"
            />
            <Text strong className="hero-text">
              AI Assistant
            </Text>
            <Text className="hero-subtext">
              24/7 Support Available
            </Text>
            <motion.div whileTap={{ scale: 0.95 }}>
              <Button
                shape="round"
                icon={<PhoneOutlined />}
                className="hero-support-btn"
                style={{ marginTop: 16, padding: '8px 20px', height: '44px' }}
              >
                Toll Free Support
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </HeroSection>
  );
});

// User Info Section Component
const UserInfo = memo(({ userData, loading }) => {
  if (loading) {
    return (
      <UserInfoSection
        {...fadeInUp}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, fontSize: 16 }}>
            Loading user information...
          </Text>
        </div>
      </UserInfoSection>
    );
  }

  return (
    <UserInfoSection
      {...fadeInUp}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <div className="hero-user-info">
        <div className="user-profile">
          <motion.div
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            <Avatar
              icon={<UserOutlined />}
              size={72}
              className="ant-avatar"
              src={userData?.avatar}
            />
          </motion.div>
          <div className="user-details">
            <Text className="user-name">
              Welcome, {userData?.name || 'User'}
            </Text>
            <Text className="user-info-item">
              <HomeOutlined />
              {userData?.location || 'Location not set'}
            </Text>
            <Text className="user-info-item">
              <SafetyOutlined />
              NH number: {userData?.nhNumber || 'Not assigned'}
            </Text>
            <Text className="user-info-item">
              <ClockCircleOutlined />
              Last Login: {userData?.lastLogin || 'N/A'}
            </Text>
          </div>
        </div>
        
        <div className="user-actions">
          <div className="status-badge">
            Active User
          </div>
          <motion.div whileTap={{ scale: 0.95 }}>
            <Tooltip title="Get immediate assistance" placement="bottom">
              <Button
                shape="round"
                icon={<MailOutlined />}
                className="contact-support-btn"
                size="small"
                style={{ height: '44px' }}
              >
                Contact Support
              </Button>
            </Tooltip>
          </motion.div>
        </div>
      </div>
    </UserInfoSection>
  );
});

// Support Cases Section Component
const SupportCases = memo(({ loading }) => {
  if (loading) {
    return (
      <SupportCasesSection
        {...fadeInUp}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="support-cases-container">
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, fontSize: 14 }}>
            Loading support cases...
          </Text>
        </div>
      </SupportCasesSection>
    );
  }

  return (
    <SupportCasesSection
      {...fadeInUp}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <div className="support-cases-container">
        <Title level={3} className="support-title">
          Support Cases
        </Title>
        
        <motion.div whileTap={{ scale: 0.95 }}>
          <Button
            type="primary"
            size="middle"
            className="support-cta-btn"
            href="/SupportCases"
            icon={<SafetyOutlined />}
          >
            View Cases
          </Button>
        </motion.div>
      </div>
    </SupportCasesSection>
  );
});

const CustomCard = memo(({ title, description, icon, buttons, gradient }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      variants={fadeInUp}
    >
      <Card
        hoverable
        style={{
          borderRadius: '16px',
          boxShadow: isHovered 
            ? '0 16px 32px rgba(0, 0, 0, 0.12)' 
            : '0 6px 20px rgba(0, 0, 0, 0.06)',
          textAlign: 'center',
          height: '280px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          background: gradient || '#ffffff',
          padding: '24px 20px',
          overflow: 'hidden',
          color: gradient ? '#ffffff' : '#1e293b',
          border: gradient ? 'none' : '1px solid #e6efff',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <motion.div
          style={{ textAlign: 'center', marginBottom: '16px' }}
          animate={{ 
            scale: isHovered ? 1.08 : 1
          }}
          transition={{ type: "spring", stiffness: 300, damping: 15 }}
        >
          <Avatar
            src={icon}
            alt={`${title} Icon`}
            style={{
              width: '90px',
              height: '90px',
              filter: `drop-shadow(0 6px 12px rgba(0, 0, 0, 0.1)) ${isHovered ? 'brightness(1.1)' : ''}`,
              transition: 'filter 0.3s ease',
            }}
          />
        </motion.div>
        <div style={{ 
          marginTop: '12px', 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center' 
        }}>
          <Text strong style={{ 
            fontSize: '18px', 
            marginBottom: '10px',
            fontWeight: '800',
            color: gradient ? '#ffffff' : '#1e293b',
            lineHeight: '1.3'
          }}>
            {title}
          </Text>
          <Text style={{ 
            fontSize: '14px', 
            lineHeight: '1.4',
            color: gradient ? 'rgba(255, 255, 255, 0.9)' : '#64748b',
            fontWeight: '500'
          }}>
            {description}
          </Text>
        </div>
        <Space size={12} style={{ 
          marginTop: 'auto', 
          justifyContent: 'center',
          paddingTop: '16px',
          flexWrap: 'wrap'
        }}>
          {buttons.map((button, index) => (
            <motion.div 
              whileTap={{ scale: 0.95 }} 
              key={index}
            >
              <Button
                type="default"
                shape="round"
                href={button.href}
                style={{
                  borderColor: gradient ? 'rgba(255, 255, 255, 0.4)' : '#e0e7ff',
                  padding: '8px 18px',
                  fontWeight: 700,
                  fontSize: '13px',
                  background: gradient ? '#ffffff' : '#f9fafb',
                  color: gradient ? '#3b82f6' : '#3b82f6',
                  borderRadius: '12px',
                  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
                  border: gradient ? 'none' : '2px solid #e0e7ff',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: '44px',
                  minWidth: '80px'
                }}
              >
                {button.text}
              </Button>
            </motion.div>
          ))}
        </Space>
      </Card>
    </motion.div>
  );
});

// Error Boundary Component
const ErrorBoundary = memo(class extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <motion.div 
          style={{ textAlign: 'center', padding: '120px 20px' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Text type="danger" style={{ fontSize: '18px', marginBottom: '20px' }}>
            Something went wrong. Please try again.
          </Text>
          <Button
            type="primary"
            onClick={() => window.location.reload()}
            style={{ 
              marginTop: 16, 
              padding: '12px 24px',
              height: '44px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '15px'
            }}
          >
            Retry
          </Button>
        </motion.div>
      );
    }
    return this.props.children;
  }
});

const Home = () => {
  const navigate = useNavigate();
  const { userData, loading, error } = useUserData();

  const handleLogout = async () => {
    try {
      const response = await fetch('/backend/logout.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        message.success({
          content: data.message || 'Logout successful',
          duration: 2.5,
        });
        navigate('/login');
      } else {
        message.error({
          content: data.error || 'Error during logout.',
          duration: 2.5,
        });
      }
    } catch (error) {
      message.error({
        content: 'An error occurred during logout.',
        duration: 2.5,
      });
    }
  };

  if (error) {
    return (
      <StyledLayout>
        <motion.div 
          style={{ textAlign: 'center', padding: '120px 20px' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Text type="danger" style={{ fontSize: '16px' }}>{error}</Text>
          <Button 
            type="primary" 
            onClick={() => window.location.reload()} 
            style={{ 
              marginTop: 20, 
              padding: '12px 24px',
              height: '44px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Retry
          </Button>
        </motion.div>
      </StyledLayout>
    );
  }

  return (
    <AnimatePresence>
      <StyledLayout>
        <ErrorBoundary>
          <NavigationHeader 
            onLogout={handleLogout} 
            userData={userData} 
            loading={loading} 
          />
          
          <StyledContent>
            <HeroBanner loading={loading} />
            
            <UserInfo userData={userData} loading={loading} />
            
            <SupportCases loading={loading} />
            
            <CardsSection>
              <motion.div 
                className="section-title"
                variants={fadeInUp}
                initial="initial"
                animate="animate"
              >
                <Title level={2}>Your Services & Resources</Title>
                <Text>
                  Access comprehensive support services designed to help you navigate your journey to stable housing
                </Text>
              </motion.div>
              
              <motion.div
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Manage Profile"
                      description="Manage your personal info, housing status, and contacts."
                      icon="assets/3.png"
                      buttons={[
                        { text: 'My Profile', href: '/profile' },
                        { text: 'Contacts', href: '/ContactForm' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Support Services"
                      description="Identify needs and manage activities seamlessly."
                      icon="assets/4.png"
                      buttons={[
                        { text: 'Support Needs', href: '/ServiceProviders' },
                        { text: 'Activities', href: '/ServiceProviders' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Privacy & Data"
                      description="Control who accesses your personal data securely."
                      icon="assets/6.png"
                      buttons={[
                        { text: 'View Settings', href: '/DataPrivacy' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Emergency Shelter"
                      description="Find emergency beds and safe parking quickly."
                      icon="assets/7.png"
                      gradient="linear-gradient(135deg, #a3bffa 0%, #93c5fd 100%)"
                      buttons={[
                        { text: 'Find Shelter', href: '/ServiceProviders' },
                        { text: 'Emergency', href: 'http://nhguide.net' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Communicate"
                      description="Reach out via 211 or message a service provider."
                      icon="assets/6.png"
                      gradient="linear-gradient(135deg, #a3bffa 0%, #93c5fd 100%)"
                      buttons={[
                        { text: 'Call 211', href: '/events' },
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <CustomCard
                      title="Tips and Courses"
                      description="Learn from lived experiences with engaging courses."
                      icon="assets/5.png"
                      gradient="linear-gradient(135deg, #a3bffa 0%, #93c5fd 100%)"
                      buttons={[
                        { text: 'View Courses', href: 'http://nhguide.net' },
                      ]}
                    />
                  </Col>
                </Row>
              </motion.div>
            </CardsSection>
          </StyledContent>
          
          <StyledFooter>
            <Space size={28} wrap>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button type="link" href="https://nada.gg/ACHRAI" target="_blank">
                  ACHRAI COC AI
                </Button>
              </motion.div>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button type="link" href="/DataPrivacy">
                  Privacy Policy
                </Button>
              </motion.div>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button type="link" href="https://nhguide.org/courses/">
                  Courses
                </Button>
              </motion.div>
            </Space>
          </StyledFooter>
        </ErrorBoundary>
      </StyledLayout>
    </AnimatePresence>
  );
};

export default Home;